import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import imageUrlBuilder from '@sanity/image-url';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { PortableText } from '@portabletext/react';
import { config } from '@/config/environment';

const builder = imageUrlBuilder(sanityClient);
function urlFor(source: any) {
  return builder.image(source).url();
}

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  padding: 13% 12.5% calc(10.51% - 32px) 12.5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 98px 20px;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 50px;
  line-height: 109.3%;
  color: #ffffff;
  margin-bottom: 60px;
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 32px;
  & a {
    color: #4497aa;
  }
`;
const Supporters = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  column-gap: 2.9%;
  row-gap: 32px;
  @media (max-width: 600px) {
    column-gap: unset;
    justify-content: space-between;
  }
`;
const SupporterLogo = styled.img`
  width: 111px;
  height: 111px;
  @media (max-width: 600px) {
    margin: 0 10px;
  }
`;

interface Supporter {
  logo: any;
  alt: string;
}

interface PartnersDocument {
  _id: string;
  title: string;
  description: any[];
  supporters: Supporter[];
}

const Partners = () => {
  const [partnersData, setPartnersData] = useState<PartnersDocument | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const archiveMode = config.platformMode === 'archive';

  useEffect(() => {
    const fetchPartners = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="partners"][0]{ _id, title, description, supporters[] { logo, alt } }`,
      );
      setPartnersData(data);
      setLoading(false);
    };

    fetchPartners();
  }, []);

  if (loading || !partnersData) {
    return <MainDiv>Loading...</MainDiv>;
  }

  return (
    <MainDiv>
      <Title>{partnersData.title}</Title>
      <Text>
        {archiveMode ? (
          <>
            <p>
              Curiko was founded by four organizations: three of British Columbia&apos;s largest
              community living associations and one small social design agency. They came together
              in 2014 because they believed the status quo for adults with cognitive disabilities
              wasn&apos;t good enough.
            </p>
            <p>
              Throughout its journey, Curiko was made possible through the generous support and
              contributions of these organizations.
            </p>
          </>
        ) : (
          <PortableText
            value={partnersData.description}
            components={{
              marks: {
                link: ({ children, value }: any) => <a href={value.href}>{children}</a>,
                strong: ({ children }: any) => <strong>{children}</strong>,
              },
              block: {
                normal: ({ children }: any) => <p>{children}</p>,
              },
            }}
          />
        )}
      </Text>
      <Supporters>
        {partnersData.supporters.map((supporter, index) => (
          <SupporterLogo key={index} src={urlFor(supporter.logo)} alt={supporter.alt} />
        ))}
      </Supporters>
    </MainDiv>
  );
};

export default Partners;
